import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import { usePets, Pet } from "@/hooks/usePets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Camera, Upload } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface EditarPetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pet: Pet;
}

interface PetFormData {
  name: string;
  type: string;
  breed: string;
  color: string;
  birth_date: Date | undefined;
  unit_number: string;
  visible_to_residents: boolean;
  sex: string;
  size: string;
  weight: string;
  observations: string;
}

export function EditarPetModal({ open, onOpenChange, pet }: EditarPetModalProps) {
  const { updatePet, uploadPhoto, isUpdating } = usePets();
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [selectedVaccinationCard, setSelectedVaccinationCard] = useState<File | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const vaccinationInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<PetFormData>();

  const watchedBirthDate = watch("birth_date");
  const watchedVisible = watch("visible_to_residents");

  const petTypes = [
    "Cachorro",
    "Gato",
    "Pássaro",
    "Peixe",
    "Hamster",
    "Coelho",
    "Outro"
  ];

  const petSizes = [
    "Pequeno",
    "Médio",
    "Grande",
    "Gigante"
  ];

  useEffect(() => {
    if (pet) {
      reset({
        name: pet.name,
        type: pet.type,
        breed: pet.breed || "",
        color: pet.color || "",
        birth_date: pet.birth_date ? new Date(pet.birth_date) : undefined,
        unit_number: pet.unit_number || "",
        visible_to_residents: pet.visible_to_residents,
        sex: pet.sex || "",
        size: pet.size || "",
        weight: pet.weight?.toString() || "",
        observations: pet.observations || "",
      });
    }
  }, [pet, reset]);

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedPhoto(file);
    }
  };

  const handleVaccinationSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedVaccinationCard(file);
    }
  };

  const onSubmit = async (data: PetFormData) => {
    try {
      const updates: Partial<Pet> = {
        id: pet.id,
        name: data.name,
        type: data.type,
        breed: data.breed || undefined,
        color: data.color || undefined,
        birth_date: data.birth_date ? format(data.birth_date, "yyyy-MM-dd") : undefined,
        unit_number: data.unit_number || undefined,
        visible_to_residents: data.visible_to_residents,
        sex: data.sex || undefined,
        size: data.size || undefined,
        weight: data.weight ? parseFloat(data.weight) : undefined,
        observations: data.observations || undefined,
      };

      // Upload da foto se selecionada
      if (selectedPhoto) {
        const photoUrl = await uploadPhoto(selectedPhoto, pet.id, 'photo');
        updates.photo_url = photoUrl;
      }

      // Upload da carteira de vacinação se selecionada
      if (selectedVaccinationCard) {
        const vaccinationUrl = await uploadPhoto(selectedVaccinationCard, pet.id, 'vaccination_card');
        updates.vaccination_card_url = vaccinationUrl;
      }

      updatePet({ ...updates, id: pet.id }, {
        onSuccess: () => {
          setSelectedPhoto(null);
          setSelectedVaccinationCard(null);
          onOpenChange(false);
        }
      });
    } catch (error) {
      console.error("Erro ao atualizar pet:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Pet</DialogTitle>
          <DialogDescription>
            Atualize as informações do pet {pet?.name}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Informações Básicas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome do Pet *</Label>
              <Input
                id="name"
                {...register("name", { required: "Nome é obrigatório" })}
                placeholder="Ex: Rex, Mimi"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select value={watch("type")} onValueChange={(value) => setValue("type", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {petTypes.map((type) => (
                    <SelectItem key={type} value={type.toLowerCase()}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="breed">Raça</Label>
              <Input
                id="breed"
                {...register("breed")}
                placeholder="Ex: Labrador, Persa"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="color">Cor</Label>
              <Input
                id="color"
                {...register("color")}
                placeholder="Ex: Marrom, Preto e branco"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data de Nascimento</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !watchedBirthDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {watchedBirthDate ? (
                      format(watchedBirthDate, "dd/MM/yyyy")
                    ) : (
                      <span>Selecione uma data</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={watchedBirthDate}
                    onSelect={(date) => setValue("birth_date", date)}
                    disabled={(date) => date > new Date()}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit_number">Unidade</Label>
              <Input
                id="unit_number"
                {...register("unit_number")}
                placeholder="Ex: Apt 101, Casa 5"
              />
            </div>
          </div>

          {/* Detalhes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Sexo</Label>
              <Select value={watch("sex")} onValueChange={(value) => setValue("sex", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="macho">Macho</SelectItem>
                  <SelectItem value="femea">Fêmea</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Porte</Label>
              <Select value={watch("size")} onValueChange={(value) => setValue("size", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {petSizes.map((size) => (
                    <SelectItem key={size} value={size.toLowerCase()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="weight">Peso (kg)</Label>
              <Input
                id="weight"
                type="number"
                step="0.1"
                {...register("weight")}
                placeholder="Ex: 5.5"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observations">Observações</Label>
            <Textarea
              id="observations"
              {...register("observations")}
              placeholder="Informações adicionais sobre o pet..."
              rows={3}
            />
          </div>

          {/* Visibilidade */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="visible"
              checked={watchedVisible}
              onCheckedChange={(checked) => setValue("visible_to_residents", !!checked)}
            />
            <Label htmlFor="visible">Visível para todos os moradores</Label>
          </div>

          {/* Upload de Fotos */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Foto do Pet</Label>
              {pet.photo_url && (
                <div className="mb-2">
                  <img
                    src={pet.photo_url}
                    alt={pet.name}
                    className="w-20 h-20 object-cover rounded-md"
                  />
                  <p className="text-xs text-muted-foreground">Foto atual</p>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => photoInputRef.current?.click()}
                  className="flex-1"
                >
                  <Camera className="mr-2 h-4 w-4" />
                  {selectedPhoto ? selectedPhoto.name : "Alterar Foto"}
                </Button>
              </div>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoSelect}
                className="hidden"
              />
            </div>

            <div className="space-y-2">
              <Label>Carteira de Vacinação</Label>
              {pet.vaccination_card_url && (
                <div className="mb-2">
                  <a
                    href={pet.vaccination_card_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Ver carteira atual
                  </a>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => vaccinationInputRef.current?.click()}
                  className="flex-1"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {selectedVaccinationCard ? selectedVaccinationCard.name : "Alterar Carteira"}
                </Button>
              </div>
              <input
                ref={vaccinationInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleVaccinationSelect}
                className="hidden"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isUpdating}>
              {isUpdating ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}